import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { selectHasActivePartner, selectIsPendingValidation, useCurrentUser } from '../../../lib/auth';
import { canEditPendingCompanySettings, useValidation } from '../../validation';
import { partnerCriteriaQueryOptions } from '../api/partnerCriteriaApi';
import { useCompanyProfile } from '../api/profileApi';
import { useWorkCategorySelection } from '../api/workCategoriesApi';
import { useWorkRegionSelection } from '../api/workRegionsApi';
import { CompanyProfileCard } from '../components/CompanyProfileCard';
import { PartnerCriteriaCard } from '../components/PartnerCriteriaCard';
import { SettingsQueryGate } from '../components/SettingsQueryGate';
import { WorkCategoriesCard } from '../components/WorkCategoriesCard';
import { WorkRegionCard } from '../components/WorkRegionCard';

export function CompanySettingsPage(): ReactElement {
    const { t } = useTranslation();
    const currentUser = useCurrentUser();
    const isPendingValidation = selectIsPendingValidation(currentUser);
    const hasPartner = selectHasActivePartner(currentUser);
    const profileQuery = useCompanyProfile();
    const workCategorySelectionQuery = useWorkCategorySelection();
    const workRegionSelectionQuery = useWorkRegionSelection();
    const partnerCriteriaQuery = useQuery({ ...partnerCriteriaQueryOptions, enabled: hasPartner });
    const validationState = useValidation().data?.validation?.state;

    if (profileQuery.isError || workCategorySelectionQuery.isError || workRegionSelectionQuery.isError) {
        return <Alert severity="error">{t('SETTINGS.ERRORS.LOAD_SETTINGS')}</Alert>;
    }

    if (!profileQuery.data || !workCategorySelectionQuery.data || !workRegionSelectionQuery.data) {
        return (
            <Stack spacing={{ xs: 2, md: 3 }} aria-busy>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={260} />
                ))}
            </Stack>
        );
    }

    const isLocked =
        isPendingValidation && validationState !== undefined && !canEditPendingCompanySettings(validationState);

    return (
        <Stack spacing={{ xs: 2, md: 3 }}>
            {isPendingValidation ? (
                <Alert severity="info">
                    {isLocked ? t('SETTINGS.COMPANY.PENDING.LOCKED') : t('SETTINGS.COMPANY.PENDING.EDITABLE')}
                </Alert>
            ) : undefined}
            <CompanyProfileCard
                profile={profileQuery.data}
                readOnly={isLocked}
                withoutLogoAndDescription={isPendingValidation}
                withNameFromRegister={isPendingValidation}
            />
            <WorkCategoriesCard selectedLeafIds={workCategorySelectionQuery.data} readOnly={isLocked} />
            <WorkRegionCard region={workRegionSelectionQuery.data} readOnly={isLocked} />
            {hasPartner ? (
                <SettingsQueryGate query={partnerCriteriaQuery} loading={<Skeleton variant="rounded" height={260} />}>
                    {(criteria) => <PartnerCriteriaCard criteria={criteria} />}
                </SettingsQueryGate>
            ) : undefined}
        </Stack>
    );
}
